import {
  BMButton,
  BMComponentsType,
  BMInputField,
  BMPrimitives,
  BMText,
  BMTray,
  useBMTheme,
} from "@traveloka/district-bloom";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dimensions,
  FlatList,
  KeyboardAvoidingView,
  Pressable,
  StyleSheet,
} from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { ErrorSection } from "../../Error";
import { BMDropdownOptionRow, buildOptionRows } from "../groupRows";
import { useGetValue } from "../hooks";
import { BMDropdownMobileOptionsProps, BMDropdownOptionsValue } from "../types";

import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import htmr from "htmr";

import { appendTestId } from "../../../utils";
import DotLoader from "../../DotLoader";
import { useScrollLock } from "../../hooks";
import { ModalManager } from "../../Modal";
import BMView from "../../View/BMView";
import BMDropdownGroupSelectRow from "./BMDropdownGroupSelectRow";
import BMDropdownNotFoundOptions from "./BMDropdownNotFoundOptions";
import BMDropdownOptionItem from "./BMDropdownOptionItem";

const CONTENT_MAX_HEIGHT = Dimensions.get("window").height * 0.32;
const ITEM_HEIGHT = 48;
const TRAY_OPEN_ANIMATION_MS = 400;

/** Normalizes the single-or-array `selected` prop into one array. */
function resolveItems<T>(
  selected: BMDropdownOptionsValue<T> | BMDropdownOptionsValue<T>[] | null
): BMDropdownOptionsValue<T>[] {
  return Array.isArray(selected) ? selected : selected ? [selected] : [];
}

export default function BMDropdownMobileOptions<T>({
  selected: initialSelected,
  show,
  option,
  isRadio,
  isMultiple,
  optionsSection,
  searchable,
  onClose,
  onChange,
  getOptionValue,
  renderSelectionItem,
  testID,
  error,
}: BMDropdownMobileOptionsProps<T>) {
  const [query, setQuery] = useState("");
  const [isVisible, setIsVisible] = useState(show);
  /**
   * `selected` is an array when isMultiple, a single value otherwise.
   * Stage as one array (0-or-1 items for single-select) so the selection
   * logic below is unconditional.
   */
  const [stagedItems, setStagedItems] = useState<BMDropdownOptionsValue<T>[]>(
    resolveItems(initialSelected)
  );

  const { placeholder, noResultCaption, NoResultFooterComponent } =
    typeof searchable !== "boolean" && searchable
      ? searchable
      : {
          placeholder: undefined,
          noResultCaption: undefined,
          NoResultFooterComponent: null,
        };

  const { BMComponents } = useBMTheme();
  const { CorporateGeneralErrorMessage: cr } = useMobileContentResource();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const trayId = useId();
  const getValue = useGetValue(getOptionValue);
  useScrollLock(!ModalManager.empty);

  const filteredOptions = useMemo(
    () =>
      option.items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
      ),
    [query, option.items]
  );

  const rows = useMemo(
    () => buildOptionRows(option.items, filteredOptions),
    [option.items, filteredOptions]
  );

  useEffect(() => {
    setIsVisible(show);
    if (show) {
      setQuery("");
      setStagedItems(resolveItems(initialSelected));
      ModalManager.add(trayId, setIsVisible);
    } else {
      ModalManager.remove(trayId);
    }
  }, [show, initialSelected, trayId]);

  const isItemSelected = useCallback(
    (item: BMDropdownOptionsValue<T>) =>
      stagedItems.some((v) => getValue(v.value) === getValue(item.value)),
    [stagedItems, getValue]
  );

  const selectedIndex = useMemo(
    () =>
      rows.findIndex((row) => row.type === "item" && isItemSelected(row.item)),
    [rows, isItemSelected]
  );

  const flatListRef = useRef<FlatList<BMDropdownOptionRow<T>>>(null);

  useEffect(() => {
    if (!isVisible || selectedIndex <= 0) return;

    // BMTray's open animation (BMTray.tsx handleOpen) hasn't settled when
    // FlatList's initialScrollIndex would fire, leaving the pre-selected rows'
    // spacer visible as blank space. Scroll manually once the animation ends;
    // keyed on isVisible only so this doesn't re-run on every selection change.
    const timeoutId = setTimeout(() => {
      flatListRef.current?.scrollToIndex({
        index: selectedIndex,
        animated: true,
        viewPosition: 0,
      });
    }, TRAY_OPEN_ANIMATION_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleOptionPress = useCallback(
    (item: BMDropdownOptionsValue<T>) => {
      if (!isMultiple) {
        setStagedItems([item]);
        return;
      }
      const next = isItemSelected(item)
        ? stagedItems.filter((v) => getValue(v.value) !== getValue(item.value))
        : [...stagedItems, item];
      setStagedItems(next);
    },
    [isMultiple, isItemSelected, stagedItems, getValue]
  );

  const renderRow = useCallback(
    ({ item: row, index }: { item: BMDropdownOptionRow<T>; index: number }) => {
      if (row.type === "group") {
        const isSelected = row.items.every((i) => isItemSelected(i));
        return (
          <BMView
            style={
              !error && isSelected
                ? {
                    backgroundColor:
                      BMComponents.BMStyleSurfaceBgcolorHighlight,
                  }
                : {}
            }
          >
            <BMDropdownGroupSelectRow
              label={row.label}
              items={row.items}
              selectedItems={stagedItems}
              getValue={getValue}
              onPress={setStagedItems}
            />
          </BMView>
        );
      }
      const isSelected = isItemSelected(row.item);
      return (
        <BMView
          style={[
            !error && isSelected
              ? { backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight }
              : {},
            row.indent ? styles.groupIndent : undefined,
          ]}
        >
          <Pressable
            onPress={() => handleOptionPress(row.item)}
            testID={appendTestId(testID, `option-${index}`)}
          >
            {renderSelectionItem && isSelected ? (
              renderSelectionItem(row.item)
            ) : (
              <BMDropdownOptionItem
                isMobile
                query={query}
                isRadio={isRadio}
                isMultiple={isMultiple}
                label={row.item.label}
                options={row.item.options}
                isSelected={isSelected}
              />
            )}
          </Pressable>
        </BMView>
      );
    },
    [
      isItemSelected,
      BMComponents,
      styles.groupIndent,
      testID,
      renderSelectionItem,
      query,
      isRadio,
      isMultiple,
      stagedItems,
      error,
      getValue,
      handleOptionPress,
    ]
  );

  function renderContent() {
    if (option.isLoading) {
      return (
        <BMView style={styles.marginVerticalLG}>
          <DotLoader />
        </BMView>
      );
    }

    if (option.isError) {
      return (
        <ErrorSection
          type="LARGE"
          title={cr.perSectionTitle}
          caption={cr.perSectionDescription}
          button={{
            type: "PRIMARY",
            label: cr.perSectionCTA,
            onPress: option.onClickRetry ?? (() => {}),
          }}
          style={styles.errorWrapperStyle}
        />
      );
    }

    if (filteredOptions.length > 0) {
      return (
        <FlatList
          ref={flatListRef}
          data={rows}
          renderItem={renderRow}
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: CONTENT_MAX_HEIGHT }}
          ItemSeparatorComponent={() => <BMView style={styles.divider} />}
          getItemLayout={(_, index) => ({
            length: ITEM_HEIGHT,
            offset: ITEM_HEIGHT * index,
            index,
          })}
          keyExtractor={(row) => row.key}
        />
      );
    }

    return (
      <BMDropdownNotFoundOptions
        isMobile
        query={query}
        noResultCaption={noResultCaption}
        NoResultFooterComponent={
          typeof NoResultFooterComponent === "function"
            ? NoResultFooterComponent
            : undefined
        }
      />
    );
  }

  function renderOptionsSection() {
    if (!optionsSection?.title && !optionsSection?.description) return null;

    const optionDescription = optionsSection.description
      ? htmr(optionsSection.description, {
          transform: {
            b: (node) => (
              <BMText variant="uiSmallEm" ink="BMStyleTextInkSecondary">
                {node.children}
              </BMText>
            ),
          },
        })
      : null;

    return (
      <BMView spacing="md" style={styles.marginTopMD}>
        {optionsSection.title && (
          <BMText variant="titleNormal">{optionsSection.title}</BMText>
        )}
        {optionDescription && (
          <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
            {optionDescription}
          </BMText>
        )}
      </BMView>
    );
  }

  useEffect(() => {
    return () => ModalManager.remove(trayId);
  }, [trayId]);

  return (
    <BMTray
      fitContent
      isVisible={isVisible}
      onTrayClose={onClose}
      {...(option.isLoading || !option.isError
        ? {
            footerComponent: () => (
              <BMDropdownMobileOptionsFooter
                hasCancelButton={optionsSection?.hasCancelButton}
                onClose={onClose}
                onConfirm={() =>
                  onChange(isMultiple ? stagedItems : (stagedItems[0] ?? null))
                }
                testID={testID}
              />
            ),
          }
        : {})}
    >
      <BMView spacing="xl" style={optionsSection?.wrapperStyles}>
        {(optionsSection || searchable) && (
          <BMView spacing="md">
            {renderOptionsSection()}
            {!option.isLoading && !option.isError && searchable && (
              <KeyboardAvoidingView>
                <BMInputField
                  value={query}
                  LeadingIcon={IcSystemSearch}
                  placeholder={placeholder ? placeholder : "Search..."}
                  onChange={(e) => setQuery(e.nativeEvent.text)}
                />
              </KeyboardAvoidingView>
            )}
          </BMView>
        )}
        <BMView spacing="sm">
          <BMView
            style={[
              error ? styles.errorGroup : undefined,
              optionsSection?.contentStyles,
            ]}
          >
            {renderContent()}
          </BMView>
          {typeof error === "string" && (
            <BMText variant="uiSmall" ink="BMUiInputInkError">
              {error}
            </BMText>
          )}
        </BMView>
      </BMView>
    </BMTray>
  );
}

type BMDropdownMobileOptionsFooterProps = {
  onClose: () => void;
  onConfirm: () => void;
  hasCancelButton?: boolean;
  testID?: string;
};

function BMDropdownMobileOptionsFooter({
  onClose,
  onConfirm,
  testID,
  hasCancelButton = true,
}: BMDropdownMobileOptionsFooterProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const { CorporateCommon: cr } = useMobileContentResource();
  return (
    <BMView row spacing="lg" style={styles.footerWrapper}>
      {hasCancelButton && (
        <BMView flex>
          <BMButton
            role="SECONDARY"
            size="LARGE"
            label={cr.cancelText}
            onPress={onClose}
            stretchable
            testID={appendTestId(testID, "cancel")}
          />
        </BMView>
      )}
      <BMView flex>
        <BMButton
          role="PRIMARY"
          size="LARGE"
          label={cr.confirmText}
          onPress={onConfirm}
          stretchable
          testID={appendTestId(testID, "confirm")}
        />
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    marginTopMD: {
      marginTop: BMPrimitives.BMSize.md,
    },
    marginVerticalLG: {
      marginVertical: BMPrimitives.BMSize.lg,
    },
    marginBottomXL5: {
      marginBottom: BMPrimitives.BMSize.xl5,
    },
    footerWrapper: {
      margin: -BMPrimitives.BMSize.xl,
      padding: BMPrimitives.BMSize.xl,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight,
    },
    divider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleCardBordercolor,
    },
    groupIndent: { paddingLeft: BMPrimitives.BMSize.lg },
    errorWrapperStyle: {
      minWidth: "auto",
      marginHorizontal: -BMPrimitives.BMSize.md,
    },
    errorGroup: {
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorError,
      backgroundColor: BMComponents.BMUiControlBgcolorGroupError,
    },
    notFoundIllustration: {
      width: 48,
      height: 48,
    },
  });
}
